import { useRef, useState } from 'react';
import {
  companionFamily,
  companionFamilyAsk,
  companionFamilyResidents,
  companionMomentSend,
  companionMoments,
  type CompanionMoment,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { A, Sample, dayWord, firstName, monthsSince, st, useApi } from './ui';
import { useCustomerText } from '../../ui/buildLabels';

// Atlas as Family see it (design T255-374, isFamilyCompanion): only what she agreed
// to share, what she said no to shown as calmly as a yes, and the one action that is
// theirs: sending her something. Everything comes from the routes; nothing is a guess.

type Kind = 'msg' | 'photo' | 'voice';
const KINDS: [Kind, string][] = [
  ['msg', 'Message'],
  ['photo', 'Photo'],
  ['voice', 'Voice note'],
];
const LABEL: Record<string, string> = { text: 'Message', photo: 'Photo', voice: 'Voice note', video: 'Video' };

const H = 'font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;';

/** "She agreed 8 months ago": how long ago she said yes. */
export function agreedAgo(iso: string, now = Date.now()): string {
  const m = monthsSince(iso, now);
  return m === 0 ? 'She agreed just now' : `She agreed ${m} ${m === 1 ? 'month' : 'months'} ago`;
}

/** Where a message is: waiting for a good moment, or seen (and what she sent back). */
export function momentStatus(m: CompanionMoment, now = Date.now()): { text: string; color: string } {
  if (!m.shownAt) return { text: 'Waiting for a good moment to give it to her', color: '#54606C' };
  return { text: `She saw it ${dayWord(m.shownAt, now)}${m.replyNote ? ` and ${m.replyNote}` : ''}`, color: '#1F6B52' };
}

export function FamilyCompanion() {
  const plain = useCustomerText();
  const { client, session } = useSession();
  const mine = useApi((c, signal) => companionFamilyResidents(c, { signal }));
  const residentId = mine.data?.residents[0]?.id ?? null;
  const fam = useApi((c, signal) => companionFamily(c, { params: { residentId: residentId! }, signal }), [residentId], !!residentId);
  const moments = useApi((c, signal) => companionMoments(c, { params: { residentId: residentId! }, signal }), [residentId], !!residentId);
  const [kind, setKind] = useState<Kind>('msg');
  const [draft, setDraft] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [rec, setRec] = useState<0 | 1 | 2>(0);
  const [voice, setVoice] = useState<Blob | null>(null);
  const [asked, setAsked] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);

  const f = fam.data;
  const name = f?.firstName ?? firstName(mine.data?.residents[0]?.name);
  const ready = draft.trim().length > 0 || (kind === 'photo' && !!photo) || (kind === 'voice' && rec === 2);
  const sent = (moments.data?.moments ?? []).filter((m) => m.source === 'family' && (!m.createdBy || m.createdBy.userId === session?.user.id));

  const send = async () => {
    if (!ready || !residentId) return;
    setErr(null);
    const mediaType = kind === 'photo' ? 'photo' : kind === 'voice' ? 'voice' : 'text';
    const file = kind === 'photo' ? photo : kind === 'voice' ? voice : null;
    try {
      await companionMomentSend(client, {
        params: { residentId },
        body: { mediaType, caption: draft.trim() || undefined, contentType: file?.type || undefined, byteSize: file?.size || undefined },
      });
      setDraft('');
      setPhoto(null);
      setVoice(null);
      setRec(0);
      moments.reload();
    } catch (e) {
      setErr((e as Error).message || 'It did not send. Try again in a moment.');
    }
  };
  const toggleRec = async () => {
    if (rec === 1) {
      recorder.current?.stop();
      return;
    }
    setErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream);
      const parts: Blob[] = [];
      r.ondataavailable = (e) => parts.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setVoice(new Blob(parts, { type: r.mimeType }));
        setRec(2);
      };
      recorder.current = r;
      r.start();
      setRec(1);
    } catch {
      setErr('The microphone is not available in this browser.');
    }
  };
  const ask = async (key: string) => {
    if (!residentId) return;
    setErr(null);
    try {
      await companionFamilyAsk(client, { params: { residentId }, body: { key } });
      setAsked((a) => ({ ...a, [key]: true }));
      // Read her answer back: a yes moves the item into what she shares, a no says so.
      fam.reload();
    } catch (e) {
      setErr((e as Error).message || 'The question was not sent.');
    }
  };

  return (
    <div data-testid="companion-family" style={st('display:flex;flex-direction:column;gap:20px;max-width:880px;min-width:0;')}>
      <div style={st('min-width:0;')}>
        <div style={st(`font-size:24px;font-weight:700;letter-spacing:-.026em;line-height:1.25;text-wrap:pretty;color:${f ? (f.status.calm ? '#12503C' : '#22272E') : '#22272E'};`)}>
          {f ? f.status.line : fam.loading || mine.loading ? '' : residentId ? plain('Her day is not served here yet.') : 'You are not linked to a resident yet.'}
          {f && <> <Sample real={f.real} figure="Family view" /></>}
        </div>
        <div style={st('font-size:14px;color:#3C454F;line-height:1.55;margin-top:6px;max-width:70ch;text-wrap:pretty;')}>
          This page only shows what she agreed to share. Where she said no, it says so rather than leaving a gap you would have to guess at.
        </div>
      </div>

      <div style={st('min-width:0;display:flex;flex-direction:column;gap:10px;padding:18px;border-radius:14px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);')}>
        <div style={st('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;min-width:0;')}>
          <span style={st('flex:0 0 auto;font-size:20px;font-weight:700;letter-spacing:-.02em;color:#22272E;')}>{`Send ${name} something`}</span>
          <span style={st('flex:1 1 240px;min-width:0;font-size:13px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
            A message, a photo or your voice. Nothing she has not agreed to share comes back the other way.
          </span>
        </div>
        <div role="tablist" style={st('display:flex;gap:6px;flex-wrap:wrap;')}>
          {KINDS.map(([k, label]) => (
            <A
              key={k}
              role="tab"
              selected={kind === k}
              onClick={() => setKind(k)}
              s={`display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;font-size:13.5px;font-weight:700;text-decoration:none;white-space:nowrap;${kind === k ? 'color:#FCFAF4;background:#1D3F6B;' : 'color:#3C454F;background:rgba(34,39,46,.06);'}`}
            >
              {label}
            </A>
          ))}
        </div>
        {kind === 'photo' && (
          <div style={st('display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;')}>
            <label style={st('flex:0 0 auto;display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;font-size:13.5px;font-weight:700;cursor:pointer;color:#1D3F6B;background:rgba(47,92,151,.11);')}>
              Choose a photo
              <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} style={st('display:none;')} />
            </label>
            <span style={st('flex:1 1 160px;min-width:0;font-size:13px;color:#3C454F;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{photo?.name ?? 'No photo chosen yet'}</span>
          </div>
        )}
        {kind === 'voice' && (
          <div style={st('display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;')}>
            <A
              write
              onClick={() => void toggleRec()}
              s={`flex:0 0 auto;display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;font-size:13.5px;font-weight:700;text-decoration:none;white-space:nowrap;${rec === 1 ? 'color:#fff;background:#B3261E;animation:coPulse 1.4s ease-out infinite;' : 'color:#1D3F6B;background:rgba(47,92,151,.11);'}`}
            >
              {rec === 1 ? 'Stop recording' : rec === 2 ? 'Record again' : 'Record'}
            </A>
            <span style={st('flex:1 1 200px;min-width:0;font-size:13px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>She hears your own voice, played by Atlas.</span>
          </div>
        )}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={kind === 'photo' ? 'Add a line about the photo (optional)' : kind === 'voice' ? 'Add a line she can read too (optional)' : 'Write to her the way you would say it'}
          aria-label={`Your message to ${name}`}
          rows={3}
          style={st('box-sizing:border-box;width:100%;min-height:84px;padding:12px 14px;border:1px solid rgba(34,39,46,.2);border-radius:10px;background:#fff;font-size:15px;line-height:1.5;color:#22272E;font-family:inherit;resize:vertical;')}
        />
        <div style={st('display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;min-width:0;')}>
          <A
            write
            disabledReason={ready ? null : 'Write something, choose a photo or record first'}
            onClick={() => void send()}
            s={`flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 20px;border-radius:10px;font-size:14.5px;font-weight:700;text-decoration:none;white-space:nowrap;${ready ? 'color:#FCFAF4;background:#1F6B52;' : 'color:#54606C;background:rgba(34,39,46,.08);'}`}
          >
            {`Send to ${name}`}
          </A>
          <span style={st('flex:1 1 260px;min-width:0;font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
            It goes to her Atlas screen, not her phone. It waits for a good moment, not during a meal or a nap, and she can reply by voice.
          </span>
        </div>
        {err && <div role="alert" style={st('font-size:16px;color:#B3261E;')}>{err}</div>}
        {sent.length > 0 && (
          <div style={st('display:flex;flex-direction:column;min-width:0;')}>
            {sent.map((m) => {
              const s = momentStatus(m);
              return (
                <div key={m.id} style={st('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;padding:10px 0;border-top:1px solid rgba(34,39,46,.12);min-width:0;')}>
                  <span style={st('flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;')}>{LABEL[m.mediaType] ?? m.mediaType}</span>
                  <span style={st('flex:1 1 200px;min-width:0;font-size:14px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>{m.caption ?? (m.mediaType === 'voice' ? 'Voice note from you' : '–')}</span>
                  <span style={st(`flex:0 1 auto;min-width:0;font-size:16px;font-weight:600;color:${s.color};text-wrap:pretty;`)}>{s.text}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {f && (
        <>
          <div style={st('min-width:0;')}>
            <div style={st(`${H}padding-bottom:4px;`)}>She mentioned you</div>
            {f.mentions.map((m, i) => (
              <div key={i} style={st('padding:10px 0;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
                <span style={st('display:block;font-size:16px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>{m.what}</span>
                <span style={st('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>{capital(dayWord(m.at))}</span>
              </div>
            ))}
            <div style={st('font-size:14px;color:#54606C;line-height:1.5;padding-top:8px;text-wrap:pretty;')}>
              These are the times your name came up. Not what she said about you, and not the conversation around it.
            </div>
          </div>

          <div style={st('min-width:0;')}>
            <div style={st('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:3px;padding-bottom:4px;')}>
              <span style={st(`flex:0 0 auto;${H}`)}>{`What ${name} agreed to share`}</span>
              <span style={st('flex:1 1 180px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;')}>each one is hers to change, and it shows how long ago she agreed</span>
            </div>
            {f.shared.map((p) => {
              const reasked = asked[p.key];
              const stale = !reasked && monthsSince(p.agreedAt) >= 6;
              return (
                <div key={p.key} style={st('display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;row-gap:5px;padding:11px 0;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
                  <span style={st('flex:1 1 200px;min-width:0;')}>
                    <span style={st('display:block;font-size:15.5px;font-weight:700;line-height:1.35;text-wrap:pretty;')}>{p.label}</span>
                    <span style={st('display:block;font-size:13.5px;color:#3C454F;line-height:1.45;margin-top:2px;text-wrap:pretty;')}>{p.value}</span>
                  </span>
                  <span style={st('flex:0 0 auto;text-align:right;')}>
                    <span style={st(`display:block;font-size:14px;font-weight:600;color:${stale ? '#4A5866' : '#54606C'};white-space:nowrap;`)}>{reasked ? 'Asked her again just now' : agreedAgo(p.agreedAt)}</span>
                    {stale && (
                      <A write onClick={() => void ask(p.key)} s="display:inline-flex;align-items:center;height:36px;padding:0 14px;margin-top:4px;border-radius:8px;font-size:13px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#0F6B63;">
                        Ask her again
                      </A>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={st('min-width:0;')}>
            <div style={st(`${H}padding-bottom:4px;`)}>What you cannot see</div>
            {f.withheld.map((w) => {
              const a = w.asked ?? (asked[w.key] ? { at: new Date().toISOString(), answer: null } : null);
              return (
                <div key={w.key} style={st('display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;row-gap:5px;padding:10px 0;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
                  <span style={st('flex:1 1 200px;min-width:0;')}>
                    <span style={st('display:block;font-size:15px;font-weight:700;line-height:1.35;color:#3C454F;text-wrap:pretty;')}>{w.label}</span>
                    <span style={st('display:block;font-size:13.5px;color:#54606C;line-height:1.45;margin-top:2px;text-wrap:pretty;')}>{w.note}</span>
                  </span>
                  {w.askable && !a && (
                    <A write onClick={() => void ask(w.key)} s="flex:0 0 auto;display:inline-flex;align-items:center;height:36px;padding:0 14px;border-radius:8px;font-size:13px;font-weight:700;text-decoration:none;white-space:nowrap;color:#1D3F6B;background:rgba(47,92,151,.11);">
                      Ask her
                    </A>
                  )}
                  {a && (
                    <span style={st(`flex:0 0 auto;font-size:16px;font-weight:600;color:${a.answer === 'yes' ? '#1F6B52' : '#54606C'};white-space:nowrap;`)}>
                      {a.answer === 'yes' ? 'She said yes' : a.answer === 'no' ? 'She said no, and that is her answer' : 'Asked. She can answer in her own time'}
                    </span>
                  )}
                </div>
              );
            })}
            <div style={st('font-size:14px;color:#54606C;line-height:1.5;padding-top:8px;max-width:82ch;text-wrap:pretty;')}>
              {`Asking sends the question to ${name}, on her own screen, in her own time. She can say no, and if she does you will see that here without anyone chasing it.`}
            </div>
          </div>

          <div style={st('min-width:0;')}>
            <div style={st(`${H}padding-bottom:4px;`)}>Who else is looking</div>
            {f.circle.map((c) => (
              <div key={c.name} style={st('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;padding:8px 0;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
                <span style={st('flex:0 0 auto;font-size:14.5px;font-weight:700;white-space:nowrap;')}>{c.name}</span>
                <span style={st('flex:0 0 auto;font-size:16px;color:#54606C;white-space:nowrap;')}>{c.relationship}</span>
                <span style={st('flex:1 1 180px;min-width:0;font-size:13px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>{c.last}</span>
              </div>
            ))}
            <div style={st('font-size:14px;color:#54606C;line-height:1.5;padding-top:8px;max-width:82ch;text-wrap:pretty;')}>
              {`Everyone here sees the same page.${f.circleNote ? ` ${f.circleNote}` : ''}`}
            </div>
          </div>

          {f.worthKnowing && (
            <div style={st('display:flex;align-items:flex-start;gap:11px;flex-wrap:wrap;padding-top:14px;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
              <span style={st('flex:0 0 auto;display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#5B6B7C;background:rgba(91,107,124,.14);')}>WORTH KNOWING</span>
              <span style={st('flex:1 1 260px;min-width:0;font-size:14px;color:#22272E;line-height:1.55;text-wrap:pretty;')}>{f.worthKnowing}</span>
            </div>
          )}
        </>
      )}

      <div style={st('font-size:14px;color:#54606C;line-height:1.55;max-width:88ch;text-wrap:pretty;')}>
        You cannot read her conversations, and you cannot direct her care from here. To raise something, contact the building.
      </div>
    </div>
  );
}

function capital(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
